<template>
  <div class="modalbox-content">
    <Loading :isActive="carregando" />

    <div class="produto-orcamentos__hint">
      <CIcon name="cil-info" size="sm" />
      Orçamentos da plataforma (B2B/Callcenter/FV) que incluem este produto (somente leitura).
    </div>

    <div v-if="!carregando && !itens.length" class="produto-orcamentos__empty">
      <CIcon name="cil-ban" size="lg" />
      <p>Nenhum orçamento encontrado para este produto</p>
    </div>

    <template v-else-if="itens.length">
      <GridCardRow>
        <GridCard
          v-for="linha in itens"
          :key="`${linha.pedido_id}-${linha.item_datahora}`"
          :cols="12"
          :titulo="`Orçamento #${linha.pedido_id}`"
          :subtitulo="linha.pedido_datahora ? formatarDataHora(linha.pedido_datahora) : null"
          accent
        >
          <template #meta>
            <span class="sistema-badge" :class="classeSituacao(linha.situacao)">
              {{ linha.situacao || '—' }}
            </span>
            <span v-if="linha.origem" class="produto-orcamentos__origem">
              {{ linha.origem }}
            </span>
          </template>

          <GridCardSection label="Cliente & filial">
            <div class="campos-grid">
              <GridCardField label="Cliente">
                <span>#{{ linha.cliente_id }}</span>
                <span class="campo-descricao">
                  {{ linha.cliente_fantasia || linha.cliente_razao_social || '—' }}
                </span>
              </GridCardField>
              <GridCardField label="Filial">
                <span>#{{ linha.filial_id }}</span>
                <span v-if="linha.filial_fantasia" class="campo-descricao">
                  {{ linha.filial_fantasia }}
                </span>
              </GridCardField>
              <GridCardField label="Colaborador">
                <span v-if="linha.colaborador_nome">{{ linha.colaborador_nome }}</span>
                <span v-else>—</span>
                <span v-if="linha.colaborador_id" class="campo-descricao">
                  #{{ linha.colaborador_id }}
                </span>
              </GridCardField>
              <GridCardField label="Validade" :valor="linha.datavalidade" tipo="date" />
            </div>
          </GridCardSection>

          <GridCardSection label="Item">
            <div class="campos-grid">
              <GridCardField label="Quantidade" :valor="linha.quantidade" tipo="numero" destaque />
              <GridCardField label="Valor unitário" :valor="linha.valorunitario" tipo="monetary" />
              <GridCardField label="Valor do item" :valor="linha.valortotal" tipo="monetary" destaque />
              <GridCardField label="Inclusão do item" :valor="linha.item_datahora" tipo="datetime" />
            </div>
          </GridCardSection>

          <GridCardSection label="Orçamento">
            <div class="campos-grid">
              <GridCardField label="Valor total do orçamento" :valor="linha.pedido_valor_total" tipo="monetary" />
            </div>
          </GridCardSection>
        </GridCard>
      </GridCardRow>

      <div class="produto-orcamentos__pagination">
        <pagination
          :data="paginador"
          :limit="3"
          @pagination-change-page="carregar"
        />
      </div>
    </template>
  </div>
</template>

<script>
import { get } from "@/helpers/apiRequest";
import { dateFilter } from "@/filters/dateFilter";
import Loading from "@/components/Loading.vue";

const SITUACAO_COR = {
  CALLCENTER: "neutro",
  CANCELADO_ERP: "perigo",
  CARRINHO: "neutro",
  EXCLUIDO: "perigo",
  ORCAMENTO: "info",
  PEDIDO: "sucesso",
  PEDIDO_MESCLADO: "sucesso",
  PEDIDO_MESCLADO_SUBCLIENTE: "sucesso",
  PEDIDO_REPROVADO_SUBCLIENTE: "perigo",
  PERDIDO: "atencao",
  PRE_PEDIDO: "info",
  PRE_PEDIDO_SUBCLIENTE: "info",
  REPRESENTANTE: "neutro",
};

export default {
  name: "ProdutoOrcamentos",
  components: { Loading },
  props: {
    item: { type: Object, required: true },
  },
  data() {
    return {
      carregando: false,
      itens: [],
      paginador: {},
    };
  },
  watch: {
    "item.id": {
      immediate: true,
      handler(produtoId) {
        if (produtoId) this.carregar(1);
      },
    },
  },
  methods: {
    carregar(pagina = 1) {
      if (!this.item.id) return;
      this.carregando = true;
      get(
        `/admin/produtos/${encodeURIComponent(this.item.id)}/orcamentos?page=${pagina}&take=15`
      )
        .then((json) => {
          const dados = json.data || json;
          this.itens = dados.itens || [];
          this.paginador = this.adaptarPaginador(dados.paginator || {});
        })
        .catch(() => {
          this.itens = [];
          this.paginador = {};
        })
        .finally(() => {
          this.carregando = false;
        });
    },
    adaptarPaginador(paginadorApi) {
      return {
        total: paginadorApi.total || 0,
        current_page: paginadorApi.current_page || 1,
        last_page: paginadorApi.last_page || 1,
        per_page: paginadorApi.per_page || 15,
        from: ((paginadorApi.current_page || 1) - 1) * (paginadorApi.per_page || 15) + 1,
        to: Math.min(
          (paginadorApi.current_page || 1) * (paginadorApi.per_page || 15),
          paginadorApi.total || 0
        ),
      };
    },
    formatarDataHora(valor) {
      return dateFilter(valor, "DD/MM/YYYY HH:mm:ss");
    },
    classeSituacao(situacao) {
      const cor = SITUACAO_COR[situacao] || "neutro";
      return `sistema-badge--${cor}`;
    },
  },
};
</script>

<style scoped>
.produto-orcamentos__hint {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 10px 12px;
  background: #f9fafb;
  border-left: 3px solid #2298a3;
  border-radius: 4px;
  font-size: 13px;
  color: #475467;
  margin-bottom: 14px;
}

.produto-orcamentos__empty {
  text-align: center;
  padding: 40px 20px;
  color: #98a2b3;
  background: #f9fafb;
  border: 1px dashed #dddce9;
  border-radius: 10px;
}

.produto-orcamentos__empty p {
  margin: 8px 0 0;
  font-size: 13px;
}

.produto-orcamentos__pagination {
  display: flex;
  justify-content: center;
  margin-top: 14px;
}

.produto-orcamentos__origem {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: #eceaf3;
  color: #3a3e81;
  border: 1px solid #dddce9;
}

.campos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 8px;
}

.campo-descricao {
  font-size: 11px;
  color: #98a2b3;
  font-weight: 400;
  margin-left: 6px;
}

.sistema-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.sistema-badge--sucesso { background: #e8f5e9; color: #2e7d32; border: 1px solid #c8e6c9; }
.sistema-badge--info    { background: #e3f2fd; color: #1565c0; border: 1px solid #bbdefb; }
.sistema-badge--atencao { background: #fff3e0; color: #e65100; border: 1px solid #ffe0b2; }
.sistema-badge--perigo  { background: #ffebee; color: #c62828; border: 1px solid #ffcdd2; }
.sistema-badge--neutro  { background: #f5f5f5; color: #666; border: 1px solid #e0e0e0; }

@media (max-width: 768px) {
  .campos-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
